import {
  APP_RELEASE_PLATFORM_LABELS,
  SupportRequestStatus,
  type AppReleasePlatform,
  type SupportRequestEntity,
  type SupportRequestFixedInVersion,
} from "@fitness-app/data-models";

export const SUPPORT_REQUESTS_QUERY_KEY = "support_requests";
export const SUPPORT_COMMENTS_QUERY_KEY = "support_request_comments";

// Terminal statuses — the trainee tab groups them as "closed" and the board asks
// for an optional closing note before moving a card here (TRE-1765 #4).
export const CLOSED_SUPPORT_STATUSES: SupportRequestStatus[] = [
  SupportRequestStatus.Done,
  SupportRequestStatus.Rejected,
];

export const BOARD_COLUMNS: SupportRequestStatus[] = [
  SupportRequestStatus.Reported,
  SupportRequestStatus.NeedsInfo,
  SupportRequestStatus.Accepted,
  SupportRequestStatus.InProgress,
  SupportRequestStatus.Qa,
  SupportRequestStatus.Done,
  SupportRequestStatus.Rejected,
];

export const STATUS_LABELS: Record<SupportRequestStatus, string> = {
  [SupportRequestStatus.Reported]: "Zgłoszenia",
  [SupportRequestStatus.NeedsInfo]: "Do wyjaśnienia",
  [SupportRequestStatus.Accepted]: "Przyjęte",
  [SupportRequestStatus.InProgress]: "W trakcie",
  [SupportRequestStatus.Qa]: "QA - Testy",
  [SupportRequestStatus.Done]: "Zamknięte",
  [SupportRequestStatus.Rejected]: "Odrzucone",
};

export const STATUS_COLORS: Record<SupportRequestStatus, string> = {
  [SupportRequestStatus.Reported]: "blue",
  [SupportRequestStatus.NeedsInfo]: "orange",
  [SupportRequestStatus.Accepted]: "geekblue",
  [SupportRequestStatus.InProgress]: "processing",
  [SupportRequestStatus.Qa]: "purple",
  [SupportRequestStatus.Done]: "green",
  [SupportRequestStatus.Rejected]: "default",
};

// antd Badge preset colors for the status select (Tag colors like "processing"
// are not valid Badge presets).
export const STATUS_BADGE_COLORS: Record<SupportRequestStatus, string> = {
  [SupportRequestStatus.Reported]: "blue",
  [SupportRequestStatus.NeedsInfo]: "orange",
  [SupportRequestStatus.Accepted]: "geekblue",
  [SupportRequestStatus.InProgress]: "cyan",
  [SupportRequestStatus.Qa]: "purple",
  [SupportRequestStatus.Done]: "green",
  [SupportRequestStatus.Rejected]: "#bfbfbf",
};

export const SOURCE_LABELS: Record<string, string> = {
  admin: "Panel",
  slack: "Slack",
  agent_chat: "Agent",
  trello_import: "Trello",
};

export interface SupportPerson {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  avatarUrl?: string | null;
}

export type SupportRequestWithRelations = SupportRequestEntity & {
  reporter: SupportPerson | null;
  assignee: SupportPerson | null;
  trainee: { id: string; firstName: string; lastName: string | null; email: string } | null;
};

// Own-property check: platform comes from jsonb, so e.g. "toString" must fall back
// to the raw string instead of resolving an inherited Object member.
export const isKnownReleasePlatform = (platform: string): platform is AppReleasePlatform =>
  Object.hasOwn(APP_RELEASE_PLATFORM_LABELS, platform);

// "iOS 2.2.0 (258) - 1.95.1" — shared by the board card chips and the drawer select.
export const releaseLabel = (platform: string, version: string) =>
  `${isKnownReleasePlatform(platform) ? APP_RELEASE_PLATFORM_LABELS[platform] : platform} ${version}`;

// support_request.fixedInVersions is jsonb — validate the shape instead of trusting it.
// The platform check is intentionally loose (string, not the enum): the endpoint
// enforces the enum on write, and entries with a legacy/unknown platform should stay
// visible (raw-label fallback) rather than silently disappear from the UI.
export const parseFixedInVersions = (value: unknown): SupportRequestFixedInVersion[] =>
  Array.isArray(value)
    ? (value as unknown[]).filter(
        (entry): entry is SupportRequestFixedInVersion =>
          typeof entry === "object" &&
          entry !== null &&
          typeof (entry as SupportRequestFixedInVersion).platform === "string" &&
          typeof (entry as SupportRequestFixedInVersion).version === "string",
      )
    : [];

export const formatPersonName = (person: SupportPerson | null) => {
  if (!person) {
    return null;
  }
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ");
  return name || person.email;
};
